import { partition } from 'lodash';
import { Moment } from 'moment';
import i18n from '@/general/i18n';
import TimeService from '@/general/services/TimeService';
import MeasurementType from '@/shared/constants/MeasurementType';
import { isPresent, present } from '@/shared/utils/typescript';
import { VehicleId } from '@/vehicle/models/Vehicle';
import AxleLocation from '../constants/AxleLocation';
import { RepairKitStatus } from '../constants/RepairKitStatus';
import ThresholdType from '../constants/ThresholdType';
import TireActionTarget from '../constants/TireActionTarget';
import TireSeason from '../constants/TireSeason';
import Axle from './Axle';
import Threshold from './Threshold';
import Tire from './Tire';
import TirePosition, { TirePositionId } from './TirePosition';

export interface RepairKitData {
  present: boolean;
  expiryDate: Moment | null;
}

export default class TireContainer {
  vehicleId!: VehicleId;
  repairKitExpiryDate: Moment | null = null;
  repairKitStatus: RepairKitStatus = RepairKitStatus.VALID;
  storageLocation: string | null = null;
  axles: Array<Axle> = [];
  thresholds: Array<Threshold> = [];

  sanitize(): void {
    this.axles.forEach(a => a.positions
      .filter(p => p.tires.every(t => !t.isMounted))
      .forEach(p => p.tires.push(Tire.withMountedStatus())));

    this.axles.filter(a => a.location !== AxleLocation.SPARE).forEach(a => a.positions
      .filter(p => p.tires.every(t => t.isMounted))
      .forEach(p => p.tires.push(new Tire())));
  }

  recalculateAxleIndexes(): void {
    const [frontAxles, rearAxles] = partition(this.axles, (a) => a.location === AxleLocation.FRONT);
    frontAxles.forEach((a, i) => a.index = i);
    rearAxles.forEach((a, i) => a.index = i);
  }

  getThresholdByType(type: MeasurementType, tireSeason?: TireSeason): Threshold | null {
    switch (type) {
      case MeasurementType.MOUNTED_TIRE_TREAD:
      case MeasurementType.STORED_TIRE_TREAD:
        switch (present(tireSeason)) {
          case TireSeason.SUMMER:
            return this.thresholds.find(t => t.type === ThresholdType.SUMMER) ?? null;
          case TireSeason.WINTER:
            return this.thresholds.find(t => t.type === ThresholdType.WINTER) ?? null;
          case TireSeason.ALL:
            return this.thresholds.find(t => t.type === ThresholdType.ALL_SEASON) ?? null;
          default:
            return null;
        }
      case MeasurementType.BRAKE_DISC_THICKNESS:
        return this.thresholds.find(t => t.type === ThresholdType.BRAKE_DISC) ?? null;
      default:
        return null;
    }
  }

  hasMountedTires(existing?: boolean): boolean {
    return this.getAllPositions().some(t => t.getMountedTire(existing));
  }

  hasStoredTires(existing?: boolean): boolean {
    return this.getAllPositions().some(t => t.getStoredTire(existing));
  }

  allStoredTiresAreKnown(): boolean {
    return this.getAllPositions().every(p => {
      return isPresent(p.getStoredTire()?.season);
    });
  }

  allMountedTiresAreKnown(): boolean {
    return this.getAllPositions().every(p => {
      return isPresent(p.getMountedTire()?.season);
    });
  }

  getAllPositions(includeSpares = false): Array<TirePosition> {
    const axles = includeSpares ? this.axles : this.axles.filter(a => a.location !== AxleLocation.SPARE);
    return axles.flatMap(a => a.positions);
  }

  getTargetPositions(position: TirePosition, target: TireActionTarget): Array<TirePosition> {
    switch (target) {
      case TireActionTarget.TIRE:
        return [position];
      case TireActionTarget.ALL:
        return this.getAllPositions();
      case TireActionTarget.AXLE:
        return present(this.axles.find(a => a.positions.some(p => p.id === position.id))).positions;
    }
  }

  getAxles(includeSpares?: boolean): Array<Axle> {
    if (includeSpares) return this.axles;
    else return this.axles.filter(a => a.location !== AxleLocation.SPARE);
  }

  getSpares(): Array<Axle> {
    return this.axles.filter(a => a.location === AxleLocation.SPARE);
  }

  getAxlesByLocation(location: AxleLocation): Array<Axle> {
    return this.axles.filter(a => a.location === location);
  }

  getAxleByPositionId(positionId: TirePositionId): Axle | null {
    return this.axles.find(a => a.positions.some(p => p.id === positionId)) ?? null;
  }

  setRepairKitStatus(repairKitData: RepairKitData): void {
    const now = TimeService.now.value;
    const warningDate = now.clone().add(6, 'months');
    const date = repairKitData.expiryDate;

    if (!repairKitData.present) this.repairKitStatus = RepairKitStatus.MISSING;
    else if (!date) this.repairKitStatus = RepairKitStatus.EMPTY;
    else if (date.isAfter(warningDate, 'day')) this.repairKitStatus = RepairKitStatus.VALID;
    else if (date.isAfter(now, 'day')) this.repairKitStatus = RepairKitStatus.WARNING;
    else {
      this.repairKitStatus =  RepairKitStatus.DANGER;
    }

    this.repairKitExpiryDate = date;
  }

  getAxleName(axle: Axle): string {
    switch (axle.location) {
      case AxleLocation.FRONT: {
        const multiple = this.getAxlesByLocation(AxleLocation.FRONT).length > 1;
        if (multiple) return i18n.global.t('tireModal.extraFrontAxle', { index: axle.index + 1 });
        else return i18n.global.t('tireModal.frontAxle');
      }
      case AxleLocation.REAR: {
        const multiple = this.getAxlesByLocation(AxleLocation.REAR).length > 1;
        if (multiple) return i18n.global.t('tireModal.extraRearAxle', { index: axle.index + 1 });
        else return i18n.global.t('tireModal.rearAxle');
      }
      default:
        return i18n.global.t('tireModal.spare');
    }
  }
}
